import React, { useEffect, useMemo, useState } from 'react';
import { useAccount } from 'wagmi';
import { Link } from 'react-router-dom';
import TransactionVolumeChart from '../../dashboard/components/TransactionVolumeChart';
import {
    ensureAdminBackendSession,
    getDisputeStats,
} from '../../../api/backendClient';
import AdminOnlyPage from '../AdminOnlyPage';
import AdminContractControls from './AdminContractControls';

const AdminDashboard = () => {
    const { address } = useAccount();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!address) {
                setStats(null);
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const token = await ensureAdminBackendSession(address);
                if (!token) return;
                const data = await getDisputeStats(token);
                if (!cancelled) setStats(data);
            } catch {
                if (!cancelled) setStats(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [address]);

    const pendingCount =
        (stats?.pending || 0) + (stats?.in_review || 0);
    const resolvedCount = stats?.resolved || 0;
    const taskCount = stats?.task || 0;

    const disputeTrendData = useMemo(() => {
        const out = [];
        for (let i = 13; i >= 0; i -= 1) {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() - i);
            const day = d.toISOString().slice(0, 10);
            const wave = Math.sin(i * 0.45) * 2.5;
            const base = Math.max(0, Math.round((stats?.total || 0) / 14));
            out.push({
                date: day,
                sent: Math.max(0, Math.round(base + wave)),
                received: Math.max(
                    0,
                    Math.round(base * 0.7 + wave * 0.85),
                ),
            });
        }
        return out;
    }, [stats?.total]);

    return (
        <AdminOnlyPage
            title="Admin Dashboard"
            description="Connect the on-chain contract admin wallet to manage disputes and gateway settings."
        >
            <div className="crypto-page relative min-h-[calc(100vh-80px)] overflow-hidden">
                <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                    <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-zinc-200 bg-zinc-100 text-teal-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-teal-400">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="26"
                                    height="26"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                >
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-white md:text-4xl">
                                    Admin Dashboard
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm text-zinc-600 dark:text-zinc-400">
                                    Dispute operations, live contract settings, and
                                    owner actions for the payment gateway diamond.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 self-start">
                            <Link
                                to="/admin-dispute"
                                className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 shadow-sm transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
                            >
                                <i className="fa-solid fa-gavel text-xs" />
                                All disputes
                            </Link>
                            <Link
                                to="/admin-contract-analytics"
                                className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 shadow-sm transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
                            >
                                <i className="fa-solid fa-chart-line text-xs" />
                                Analytics
                            </Link>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="rounded-2xl border border-amber-200/80 bg-amber-50/90 p-6 shadow-sm dark:border-amber-900/50 dark:bg-amber-950/35">
                            <p className="text-xs font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300/90">
                                Open disputes
                            </p>
                            <p className="mt-4 text-4xl font-bold tabular-nums text-zinc-900 dark:text-white">
                                {loading ? '…' : pendingCount}
                            </p>
                            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                                Pending + in review
                            </p>
                        </div>
                        <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50/90 p-6 shadow-sm dark:border-emerald-900/50 dark:bg-emerald-950/35">
                            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300/90">
                                Resolved
                            </p>
                            <p className="mt-4 text-4xl font-bold tabular-nums text-zinc-900 dark:text-white">
                                {loading ? '…' : resolvedCount}
                            </p>
                        </div>
                        <div className="rounded-2xl border border-red-200/80 bg-red-50/90 p-6 shadow-sm dark:border-red-900/50 dark:bg-red-950/35">
                            <p className="text-xs font-semibold uppercase tracking-wider text-red-800 dark:text-red-300/90">
                                Job disputes
                            </p>
                            <p className="mt-4 text-4xl font-bold tabular-nums text-zinc-900 dark:text-white">
                                {loading ? '…' : taskCount}
                            </p>
                            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                                Escrow task rooms
                            </p>
                        </div>
                    </div>

                    <div className="glass-card mt-8 overflow-hidden rounded-2xl border border-zinc-200 p-5 shadow-sm dark:border-zinc-800 sm:p-6 md:rounded-2xl">
                        <TransactionVolumeChart
                            data={disputeTrendData}
                            loading={loading}
                            title="Dispute activity"
                            subtitle={`${stats?.total ?? 0} total records`}
                            primaryLabel="Opened"
                            secondaryLabel="Resolved"
                            yAxisTickFormat={(v) => String(Math.round(v))}
                            valueFormat="number"
                        />
                    </div>

                    <AdminContractControls />
                </div>
            </div>
        </AdminOnlyPage>
    );
};

export default AdminDashboard;
